import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import { optimizationService } from '../services/optimization/OptimizationService';
import { OptimizationPrompt } from '../services/optimization/OptimizationService';
import AddPromptDialog from '../components/prompts/AddPromptDialog';
import DeletePromptDialog from '../components/prompts/DeletePromptDialog';

type PromptStatus = 'Active' | 'Inactive';

interface PromptWithStatus extends OptimizationPrompt {
  status: PromptStatus;
}

const PromptsPage: React.FC = () => {
  const [prompts, setPrompts] = useState<PromptWithStatus[]>([]);
  const [statusFilter, setStatusFilter] = useState<PromptStatus | 'all'>('all');
  const [marketplaceFilter, setMarketplaceFilter] = useState<string>('all');
  const [expandedPrompt, setExpandedPrompt] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAddPromptOpen, setIsAddPromptOpen] = useState(false);
  const [deletePrompt, setDeletePrompt] = useState<{ id: string; title: string } | null>(null);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      setIsLoading(true);
      setPrompts([]); // Clear existing prompts
      
      // Load prompts for all marketplaces
      const marketplaces = ['Shopify', 'Amazon', 'Google Shopping'] as const;
      console.log('Loading prompts for marketplaces:', marketplaces);
      
      const allPrompts: PromptWithStatus[] = [];
      
      for (const marketplace of marketplaces) {
        console.log(`Loading prompts for ${marketplace}...`);
        try {
          // Get prompts for this marketplace (including default prompt if no others exist)
          const marketplacePrompts = await optimizationService.getPrompts(marketplace);
          console.log(`Got ${marketplacePrompts.length} prompts for ${marketplace}:`, marketplacePrompts);
          
          // Add status and append to existing prompts
          const promptsWithStatus = marketplacePrompts.map(prompt => ({
            ...prompt,
            status: prompt.status || 'Active'
          }));
          
          console.log(`Adding ${promptsWithStatus.length} prompts for ${marketplace}`);
          allPrompts.push(...promptsWithStatus);
        } catch (error) {
          console.error(`Error loading prompts for ${marketplace}:`, error);
          // Try to get the default prompt directly
          try {
            const defaultPromptId = `default-${marketplace.toLowerCase().replace(/\s+/g, '-')}-prompt`;
            const defaultPrompt = await optimizationService.getPromptById(defaultPromptId);
            if (defaultPrompt) {
              console.log(`Found default prompt for ${marketplace}:`, defaultPrompt);
              allPrompts.push({
                ...defaultPrompt,
                status: defaultPrompt.status || 'Active'
              });
            }
          } catch (defaultError) {
            console.error(`Error loading default prompt for ${marketplace}:`, defaultError);
          }
        }
      }
      
      // Sort prompts by lastUsed (most recent first)
      allPrompts.sort((a, b) => {
        if (!a.lastUsed) return 1;
        if (!b.lastUsed) return -1;
        return b.lastUsed.getTime() - a.lastUsed.getTime();
      });
      
      setPrompts(allPrompts);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPrompts = prompts.filter(prompt => {
    const matchesStatus = statusFilter === 'all' || prompt.status === statusFilter;
    const matchesMarketplace = marketplaceFilter === 'all' || prompt.marketplace === marketplaceFilter;
    return matchesStatus && matchesMarketplace;
  });

  const getStatusStyle = (status: 'Active' | 'Inactive') => {
    switch (status) {
      case 'Active':
        return 'bg-green-50 text-green-800 border-green-200';
      case 'Inactive':
        return 'bg-red-50 text-red-800 border-red-200';
      default:
        return 'bg-gray-50 text-gray-800 border-gray-200';
    }
  };

  const getMarketplaceStyle = (marketplace: string) => {
    switch (marketplace) {
      case 'Shopify':
        return 'bg-green-100 text-green-900 border-green-300';
      case 'Google Shopping':
        return 'bg-blue-100 text-blue-900 border-blue-300';
      case 'Amazon':
        return 'bg-yellow-100 text-yellow-900 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-900 border-gray-300';
    }
  };

  const togglePrompt = (title: string) => {
    setExpandedPrompt(expandedPrompt === title ? null : title);
  };

  const handleStatusChange = async (promptId: string, newStatus: 'Active' | 'Inactive') => {
    if (isUpdating) return;
    
    setIsUpdating(true);
    try {
      const prompt = prompts.find(p => p.id === promptId);
      if (!prompt) return;

      // Update the status in Firestore
      await optimizationService.updatePromptStatus(promptId, newStatus);

      // Reload prompts to get the latest state from Firestore
      await loadPrompts();
    } catch (error) {
      console.error('Failed to update prompt status:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return <div className="p-8">Loading prompts...</div>;
  }

  return (
    <>
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h1 className="text-2xl font-bold text-gray-900">Prompts</h1>
              <Button onClick={() => setIsAddPromptOpen(true)} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
                <Plus className="h-4 w-4" />
                Add Prompt
              </Button>
            </div>
            
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="w-full sm:w-48">
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as PromptStatus | 'all')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>

                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-full sm:w-48">
                <Select
                  value={marketplaceFilter}
                  onValueChange={(value) => setMarketplaceFilter(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Filter by Marketplace" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Marketplaces</SelectItem>
                    <SelectItem value="Amazon">Amazon</SelectItem>
                    <SelectItem value="Google Shopping">Google Shopping</SelectItem>
                    <SelectItem value="Shopify">Shopify</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Prompts List */}
          <div className="space-y-4">
            {filteredPrompts.map((prompt) => (
              <div
                key={prompt.title}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => togglePrompt(prompt.title)}
                >
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <h3 className="text-base sm:text-lg font-medium text-gray-900">
                        {prompt.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full border leading-none whitespace-nowrap ${getMarketplaceStyle(prompt.marketplace)}`}>
                          {prompt.marketplace}
                        </span>
                        <span className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded-full border leading-none whitespace-nowrap ${getStatusStyle(prompt.status)}`}>
                          {prompt.status}
                        </span>
                        {expandedPrompt === prompt.title ? (
                          <ChevronUp className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                    {!expandedPrompt && (
                      <p className="mt-1 text-sm text-gray-500 line-clamp-1">
                        {prompt.content}
                      </p>
                    )}
                  </div>
                </div>
                
                {expandedPrompt === prompt.title && (
                  <div className="px-4 pb-4">
                    <div className="bg-gray-50 rounded-md p-4 mt-2">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700">
                        {prompt.content}
                      </pre>
                      <div className="mt-4 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(prompt.id, 'Active');
                          }}
                          disabled={isUpdating}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                            ${prompt.status === 'Active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600'}`}
                        >
                          Active
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(prompt.id, 'Inactive');
                          }}
                          disabled={isUpdating}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors
                            ${prompt.status === 'Inactive'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600'}`}
                        >
                          Inactive
                        </button>
                        {!prompt.isDefault && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeletePrompt({ id: prompt.id, title: prompt.title });
                            }}
                            disabled={isUpdating}
                            className="px-3 py-1 rounded-full text-sm font-medium transition-colors bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 flex items-center gap-1"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <AddPromptDialog
        isOpen={isAddPromptOpen}
        onOpenChange={setIsAddPromptOpen}
        onPromptAdded={() => {
          loadPrompts(); // Use the existing loadPrompts function
          setIsAddPromptOpen(false);
        }}
      />
      
      {deletePrompt && (
        <DeletePromptDialog
          isOpen={deletePrompt !== null}
          onOpenChange={(open) => !open && setDeletePrompt(null)}
          promptId={deletePrompt.id}
          promptTitle={deletePrompt.title}
          onPromptDeleted={loadPrompts}
        />
      )}
    </>
  );
};

export default PromptsPage;
